import * as styles from "./TaskList.css";

import { useAtomValue, useSetAtom } from "jotai";
import { type JSX, useMemo } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { List, ListEmpty } from "@frontend/components/atoms/List";
import { useArrayField } from "@frontend/hooks/form";
import { Trans, useLingui } from "@lingui/react/macro";

import { addTaskAtom, editedTaskAtom, jobForm } from "../stores/editor";
import { TaskDialog } from "./TaskDialog";
import { TaskRow } from "./TaskRow";

export function TaskList(): JSX.Element {
  const { t } = useLingui();

  const tasks = useArrayField(jobForm.fields.tasks);

  const edited = useAtomValue(editedTaskAtom);
  const setEdited = useSetAtom(editedTaskAtom);
  const addTask = useSetAtom(addTaskAtom);

  const originRef = useMemo(
    () => ({ current: edited?.origin ?? null }),
    [edited],
  );

  const editedItem =
    null === edited ? null : (tasks.items[edited.index] ?? null);

  const editedAtom = null === edited ? null : tasks.itemAtoms[edited.index];

  return (
    <section className={styles.list}>
      <List className={styles.rows} fade separators label={t`Tasks`}>
        {tasks.isEmpty ? (
          <ListEmpty className={styles.empty}>
            <Trans>This job has no task yet.</Trans>

            <Button
              variant="link"
              size="sm"
              leftSection={<Icon name="Plus" size="sm" />}
              onClick={(event) =>
                addTask(event.currentTarget.getBoundingClientRect())
              }
            >
              {t`Add a task`}
            </Button>
          </ListEmpty>
        ) : (
          tasks.items.map((task, index) => (
            <TaskRow
              key={task.path}
              index={index}
              itemAtom={tasks.itemAtoms[index]!}
              onOpen={(origin) =>
                setEdited({
                  index,
                  created: false,
                  origin: origin.getBoundingClientRect(),
                })
              }
              onRemove={() => tasks.remove(tasks.itemAtoms[index]!)}
            />
          ))
        )}
      </List>

      {editedItem && editedAtom && null !== edited ? (
        <TaskDialog
          key={edited.index}
          index={edited.index}
          fields={editedItem.fields}
          itemAtom={editedAtom}
          created={edited.created}
          onRemove={() => {
            tasks.remove(editedAtom);
            setEdited(null);
          }}
          originRef={originRef}
        />
      ) : null}
    </section>
  );
}
